<script setup lang="ts">
  import { VerificationForm } from '@/pages/verification';
  import { useVerificationId, verificationApi } from '@/features/verification';
  import {
    isRecordDetailRoute,
    useCustomToast,
    formatDateForApi,
    formatInstantToPtBR,
    formatCollaboratorNameAndRegistration,
    setBreadcrumbLabel,
    clearBreadcrumbLabel,
  } from '@/shared/lib';
  import type { InfoDocumento } from '@/shared/lib';
  import type { VerificationSaveData } from '@/pages/verification';
  import { computed, markRaw, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useModal } from 'vue-final-modal';
  import { CircleX } from 'lucide-vue-next';
  import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';

  import { ActionButtons, ConfirmDeleteModal, SimpleLoading, TableTitle } from '@/shared/ui';
  import { useNotificationStore } from '@/entities/Notification';
  import { VerificationDetailsView } from '@/widgets/homologation-details';
  import { getVerificationDetailConfig } from '../../config';
  import {
    buildRegistrationApprovalInfo,
    buildRegistrationElaborationInfo,
    type VerificationCardSource,
  } from '../../lib/verificationCardInfo';
  import { verificationItemGuard } from '../../model/verificationItem.guard';

  const route = useRoute();
  const router = useRouter();
  const verificationFormRef = ref<InstanceType<typeof VerificationForm> | null>(null);
  const error = ref<string | null>(null);
  const loadingVerification = ref(false);
  const verificationData = ref<VerificationSaveData | null>(null);
  const toast = useCustomToast();

  const documentRevisionId = route.params.id as string;
  const {
    documentElaborationInfo,
    documentApprovalInfo,
    loading,
    changelogLines,
    trainingInfo,
    fetchVerificationById,
    positionsAsSelectOptions,
    observationInfo,
    verification,
    selectedCollaborators,
    instructorsInfo,
    revisionLabel,
  } = useVerificationId();

  const { fetchVerificationCount } = useNotificationStore();

  const detailConfig = computed(() =>
    getVerificationDetailConfig(verification.value?.document?.type)
  );

  const verificationListRoute = computed(() => ({
    path: '/verificacao',
    query: { type: detailConfig.value.listTabType },
  }));

  // A natureza vem do segmento da rota, e vale antes de qualquer chamada: `/verificacao/61` e
  // `/verificacao/registro/61` sao itens diferentes, de tabelas diferentes.
  const isRecord = computed(() => isRecordDetailRoute(route.params.nature));

  // A Registration has no file to deliver: the card button opens the dedicated screen that draws
  // the matrix form. The id that arrives here is the matrix id, and the route segment tells them
  // apart.
  const recordViewRoute = computed(() =>
    isRecord.value
      ? { name: 'VerificacaoVerRegistro', params: { id: documentRevisionId } }
      : undefined
  );

  const cardSource = computed<VerificationCardSource>(() => ({
    requestDate: formatInstantToPtBR(verification.value?.createdAt),
    code: verification.value?.document?.prefix ?? '',
    title: verification.value?.document?.name ?? '',
    revisionLabel: revisionLabel.value,
    elaboratedBy: formatCollaboratorNameAndRegistration(
      verification.value?.document?.user?.name,
      verification.value?.document?.user?.registrationNumber
    ),
    approvedBy: formatCollaboratorNameAndRegistration(
      verification.value?.approval?.approvedBy?.name,
      verification.value?.approval?.approvedBy?.registrationNumber
    ),
    approvalDate: formatInstantToPtBR(verification.value?.approval?.approvedAt),
  }));

  const cardElaborationInfo = computed<InfoDocumento[]>(() =>
    detailConfig.value.usesRegistrationCard
      ? buildRegistrationElaborationInfo(detailConfig.value, cardSource.value)
      : documentElaborationInfo.value
  );

  const cardApprovalInfo = computed<InfoDocumento[]>(() =>
    detailConfig.value.usesRegistrationCard
      ? buildRegistrationApprovalInfo(cardSource.value)
      : documentApprovalInfo.value
  );

  const saveVerification = async (data: VerificationSaveData, status: 'APPROVED' | 'FAILED') => {
    if (!verification.value?.approval?.id) {
      throw new Error('Approval ID not found in document data');
    }

    const approvalId = verification.value.approval.id;
    const selectedPositionId = Number.isNaN(parseInt(data.selectedRole))
      ? undefined
      : parseInt(data.selectedRole);
    const effectiveDate =
      data.effectiveDate instanceof Date
        ? (formatDateForApi(data.effectiveDate) ?? undefined)
        : undefined;

    // O Registro vive em outra tabela, entao a decisao dele tem rota propria. O vinculo com a
    // aprovacao e resolvido no servidor, pela chave composta.
    if (isRecord.value) {
      await verificationApi.createMatrixVerification({
        matrixId: parseInt(data.documentRevisionId),
        status: data.approvalStatus === 'approve' ? 'VERIFIED' : 'REJECTED',
        positionId: selectedPositionId,
        justification: data.justification ?? undefined,
        effectiveDate,
      });
    } else {
      await verificationApi.createVerification({
        documentRevisionId: parseInt(data.documentRevisionId),
        approved: data.approvalStatus === 'approve',
        positionId: parseInt(data.selectedRole),
        justification: data.justification ?? undefined,
        approvalId: approvalId,
        effectiveDate,
      });
    }

    toast.showSuccessToast(
      {
        title:
          status === 'APPROVED'
            ? 'Verificação salva com sucesso.'
            : detailConfig.value.rejectSuccessTitle,
        message: status === 'APPROVED' ? undefined : 'Os usuários envolvidos serão notificados.',
        type: 'success',
      },
      {
        timeout: 4000,
      }
    );

    if (status === 'FAILED') {
      closeConfirmation();
      verificationData.value = null;
    }

    await fetchVerificationCount();
    router.push(verificationListRoute.value);
  };

  const handleSave = async (data: VerificationSaveData) => {
    loadingVerification.value = true;
    try {
      if (data.approvalStatus === 'reject') {
        verificationData.value = { ...data };
        openConfirmation();
        return;
      }

      await saveVerification(data, 'APPROVED');
    } catch {
      toast.showErrorToast({
        title: 'Erro ao salvar Verificação. Tente novamente',
        type: 'error',
      });
    } finally {
      loadingVerification.value = false;
    }
  };

  const { open: openConfirmation, close: closeConfirmation } = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      get title() {
        return detailConfig.value.rejectConfirmTitle;
      },
      description: 'A ação não pode ser desfeita.',
      icon: markRaw(CircleX),
      iconAlt: 'Reprovar',
      onClose() {
        closeConfirmation();
        loadingVerification.value = false;
      },
      async onConfirm() {
        try {
          const data = verificationData.value;
          if (!data) {
            throw new Error('No data available for verification');
          }

          await saveVerification(data, 'FAILED');
        } catch {
          toast.showErrorToast({
            title: detailConfig.value.rejectErrorMessage,
            type: 'error',
          });
          closeConfirmation();
          verificationData.value = null;
        } finally {
          loadingVerification.value = false;
        }
      },
    },
  });

  watch(
    () => verification.value?.document?.prefix,
    (itemCode) => {
      if (itemCode) {
        setBreadcrumbLabel(itemCode);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    clearBreadcrumbLabel();
  });

  // A notice for another item only changes the params, which skips the route's `beforeEnter`.
  onBeforeRouteUpdate(verificationItemGuard);

  onMounted(async () => {
    try {
      await fetchVerificationById(Number(documentRevisionId), isRecord.value);
    } catch {
      error.value = 'Erro ao carregar os dados da verificação';
    }
  });

  const handleFormSave = () => {
    if (!verificationFormRef.value) {
      error.value = 'Formulário de verificação não encontrado';
      return;
    }
    verificationFormRef.value.submit();
  };
</script>

<template>
  <div
    v-if="loading"
    class="w-full h-[80vh] flex justify-center items-center"
    data-testid="verification-detail-loading"
  >
    <SimpleLoading />
  </div>
  <div
    v-else-if="error"
    class="p-6"
  >
    <div class="text-center text-red-500">
      {{ error }}
    </div>
  </div>
  <div
    v-else
    class="flex flex-col gap-6 p-6 text-gray-07"
  >
    <h1 class="text-gray-07 text-xl/7.5 font-semibold text-left mb-6">
      Etapa de Verificação e Vigência
    </h1>
    <div
      class="overflow-x-auto bg-white-01 rounded-2xl border border-border-color divide-y divide-gray-02 text-sm"
    >
      <div class="pl-6 py-4.5 w-full text-left">
        <TableTitle :text="detailConfig.cardTitle" />
      </div>

      <VerificationDetailsView
        :changelog="changelogLines"
        :document-approval-info="cardApprovalInfo"
        :document-elaboration-info="cardElaborationInfo"
        :document-id="verification?.document?.id?.toString() as string"
        :view-route="recordViewRoute"
        :instructors-info="instructorsInfo"
        :observation="observationInfo"
        :selected-collaborators="selectedCollaborators"
        :training-justification="trainingInfo.justification"
        :training-required="trainingInfo.required"
        :view-button-label="detailConfig.viewButtonLabel"
        :show-training-section="detailConfig.hasTrainingStep"
      />

      <VerificationForm
        ref="verificationFormRef"
        :on-save="handleSave"
        :document-revision-id="documentRevisionId"
        :positions="positionsAsSelectOptions"
        :approve-option-label="detailConfig.approveOptionLabel"
        :reject-option-label="detailConfig.rejectOptionLabel"
        :requires-rejection-justification="detailConfig.requiresRejectionJustification"
        :reject-placeholder="detailConfig.rejectPlaceholder"
      />
    </div>
    <ActionButtons
      :disabled="!verificationFormRef?.isSaveButtonActive"
      :is-loading="loadingVerification"
      :cancel-route="verificationListRoute"
      @save="handleFormSave"
    />
  </div>
</template>
